import React, { useState, useEffect, useRef } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config.json";
import { S, Spin } from "./Shared";

const BulkRemoveDataOwnerModal = ({
  show,
  onHide,
  selectedKpis = [],
  onSuccess,
}) => {
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Grouped by KPI: { kpiId, kpiName, users: [ { assignmentId, userId, userName, email, uniqueKey, checked } ] }
  const [groupedKpis, setGroupedKpis] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedKpis, setExpandedKpis] = useState(new Set());

  useEffect(() => {
    if (!show) return;
    setError("");
    setSearchQuery("");

    const kpiMap = new Map();

    selectedKpis.forEach(kpi => {
      kpi.assign_raw_list?.forEach(detail => {
        detail.assignedToDetails?.forEach(u => {
          if (!kpiMap.has(kpi.id)) {
            kpiMap.set(kpi.id, { kpiId: kpi.id, kpiName: kpi.kpi_name, users: [] });
          }
          const kpiGroup = kpiMap.get(kpi.id);
          const uniqueKey = `${detail.id}-${u.id}`;
          
          if (!kpiGroup.users.some(x => x.uniqueKey === uniqueKey)) {
            kpiGroup.users.push({
              uniqueKey,
              assignmentId: detail.id,
              userId: u.id,
              userName: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email,
              email: u.email,
              checked: false,
            });
          }
        });
      });
    });

    const grouped = Array.from(kpiMap.values()).filter(k => k.users.length > 0);
    setGroupedKpis(grouped);
    setExpandedKpis(new Set(grouped.map(g => g.kpiId)));
  }, [show, selectedKpis]);

  const toggleAccordion = (kpiId) => {
    setExpandedKpis(prev => {
      const next = new Set(prev);
      if (next.has(kpiId)) next.delete(kpiId);
      else next.add(kpiId);
      return next;
    });
  };

  const handleToggle = (kpiId, uniqueKey) => {
    setGroupedKpis(prev => prev.map(kpi => {
      if (kpi.kpiId !== kpiId) return kpi;
      return {
        ...kpi,
        users: kpi.users.map(u => u.uniqueKey === uniqueKey ? { ...u, checked: !u.checked } : u)
      };
    }));
  };

  const handleToggleAll = (e) => {
    const checked = e.target.checked;
    setGroupedKpis(prev => prev.map(kpi => ({
      ...kpi,
      users: kpi.users.map(u => ({ ...u, checked }))
    })));
  };

  const handleSave = async () => {
    setError("");
    const selected = [];
    groupedKpis.forEach(kpi => {
      kpi.users.forEach(u => {
        if (u.checked) selected.push(u);
      });
    });

    if (!selected.length) {
      setError("Please select at least one user to remove.");
      return;
    }

    const assignmentIds = [...new Set(selected.map(a => a.assignmentId))];
    const userIds = [...new Set(selected.map(a => a.userId))];

    setSaving(true);
    try {
      const { isSuccess } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/assigned-kpis/unassign`,
        {},
        { assignmentIds, userIds },
        "POST"
      );

      if (isSuccess) {
        onSuccess?.();
        onHide();
      } else {
        setError("Failed to unassign users. Please try again.");
      }
    } catch {
      setError("Server error. Please try again.");
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  if (!show) return null;

  const filteredGroupedKpis = groupedKpis.map(kpi => {
    const filteredUsers = kpi.users.filter(u => 
      u.userName.toLowerCase().includes(searchQuery.toLowerCase()) || 
      u.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return { ...kpi, users: filteredUsers };
  }).filter(kpi => kpi.users.length > 0);

  const totalVisibleUsers = filteredGroupedKpis.reduce((acc, kpi) => acc + kpi.users.length, 0);
  const allChecked = totalVisibleUsers > 0 && filteredGroupedKpis.every(kpi => kpi.users.every(u => u.checked));
  const hasAnyAssignments = groupedKpis.length > 0;
  const anyChecked = groupedKpis.some(kpi => kpi.users.some(u => u.checked));

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, maxWidth: 600 }}>
        {/* Header */}
        <div style={S.header}>
          <span style={S.title}>Remove Data Owner</span>
          <button onClick={onHide} disabled={saving} style={S.closeBtn}>✕</button>
        </div>

        {/* Body */}
        <div style={S.body}>
          {error && <div style={S.errBox}>{error}</div>}

          {!hasAnyAssignments ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#64748b" }}>
              No assigned data owners found for the selected KPIs.
            </div>
          ) : (
            <>
              <div style={S.infoBox}>
                Select the users you want to unassign from the selected KPIs.
              </div>

              <div style={S.field}>
                <input
                  type="text"
                  placeholder="Search by user name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={S.dateInput}
                />
              </div>

              <div style={{
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                overflow: "hidden",
                marginTop: 10
              }}>
                <div style={{
                  display: "flex",
                  padding: "10px 16px",
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                  fontWeight: 600,
                  fontSize: 12,
                  color: "#475569",
                  alignItems: "center"
                }}>
                  <div style={{ width: 30 }}>
                    <input 
                      type="checkbox" 
                      checked={allChecked} 
                      onChange={handleToggleAll} 
                    />
                  </div>
                  <div style={{ flex: 1 }}>Select All Users</div>
                </div>
                
                <div style={{ maxHeight: 350, overflowY: "auto", padding: "10px" }}>
                  {filteredGroupedKpis.length === 0 ? (
                     <div style={{ padding: "20px", textAlign: "center", color: "#64748b", fontSize: 13 }}>
                       No users match your search.
                     </div>
                  ) : (
                    filteredGroupedKpis.map((kpi) => {
                      const isExpanded = expandedKpis.has(kpi.kpiId);
                      const hasCheckedUsers = kpi.users.some(u => u.checked);

                      return (
                        <div key={kpi.kpiId} style={{ marginBottom: 16, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                          <div 
                            onClick={() => toggleAccordion(kpi.kpiId)}
                            style={{ 
                              background: "#f8fafc", 
                              padding: "10px 16px", 
                              fontWeight: 600, 
                              borderBottom: isExpanded ? "1px solid #e2e8f0" : "none", 
                              fontSize: 13, 
                              color: "#1e293b",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              cursor: "pointer",
                              userSelect: "none"
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span>{kpi.kpiName}</span>
                              <span style={{ fontSize: 11, background: "#e2e8f0", padding: "2px 8px", borderRadius: 12, color: "#475569" }}>
                                {kpi.users.length} user{kpi.users.length !== 1 && "s"}
                              </span>
                              {hasCheckedUsers && !isExpanded && (
                                <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 700 }}>
                                  (Has selections)
                                </span>
                              )}
                            </div>
                            <span style={{ fontSize: 14, color: "#64748b", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                              ▼
                            </span>
                          </div>
                          
                          {isExpanded && (
                            <div>
                              {kpi.users.map((item, index) => (
                                <label
                                  key={item.uniqueKey}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "10px 16px",
                                    borderBottom: index < kpi.users.length - 1 ? "1px solid #f1f5f9" : "none",
                                    cursor: "pointer",
                                    background: item.checked ? "#f0fdfa" : "#fff",
                                    transition: "background 0.2s"
                                  }}
                                >
                                  <div style={{ width: 30 }}>
                                    <input
                                      type="checkbox"
                                      checked={item.checked}
                                      onChange={() => handleToggle(kpi.kpiId, item.uniqueKey)}
                                    />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {item.userName}
                                    </div>
                                    <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {item.email}
                                    </div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <button onClick={onHide} disabled={saving} style={S.btnSecondary}>
            Cancel
          </button>
          {hasAnyAssignments && (
            <button
              onClick={handleSave}
              disabled={saving || !anyChecked}
              style={{ ...S.btnDanger, opacity: saving || !anyChecked ? 0.75 : 1, cursor: saving || !anyChecked ? "not-allowed" : "pointer" }}
            >
              {saving ? (
                <>
                  <Spin light size={12} /> Removing…
                </>
              ) : (
                "Remove Selected"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkRemoveDataOwnerModal;
